import { renderClassLevelIcons } from '../../janus-engine/port/k5-render/class-level-icons';
// tabs/summary.ts — client renderer for the PC sheet's top SUMMARY BAR (the shell).
//
// Final migration step: with the summary bar a TS pane, ftRerenderPcSheet repaints it surgically on
// caster_update, so the sheet can flip data-ft-pc-owns-sheet and drop the disruptive full-AJAX refresh.
// Reproduces the bespoke PHP summary bar (table_item_player_character_inc.php ~L114-592) from the cached
// blob; reuses two existing casterCache-fed renderers via window:
//   • window.renderVManaEcacLuckBarsInline — the V/Mana/AC/Luck strip (identical to today, zero change)
//   • window.FtThumbnail.build           — the caster thumbnail (selector picture pick)
// All data values now compute from the blob (can-level-up via canLevelUpFromBlob, lineage via
// pcSlotIdName); the PHP-mount data-ft-pc-* attrs survive only as fallbacks. The remaining attrs
// (side-info / dom-suffix) are render OPTS the card renderer will pass in directly.
// Browser actions delegate through data-ft-summary-action while preserving the existing globals.

import type { CachedCaster } from '../types';
import { pcField } from '../read-helpers';
import { picturesOf } from '../../shared/array-types';
import { statusRunes, statusRuneMountHtml } from '../../shared/status-rune';
import { catalogNameById } from './ability-row';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';
import { canLevelUp } from '../../janus-engine/advancement/experience-marks';
import { paintMarkup, paintedMarkupHolds } from '../paint-markup';

// The thumbnail inputs each summary container last built from (the thumbnail is not part of the bar's markup).
const paintedThumbs = new WeakMap<HTMLElement, string>();

// ── Static catalogs (additions.php / creature-summary.ts). Order = render order. ──
const ESSENCE_CORE: ReadonlyArray<{ item_id: string; variable_name: string }> = [
    { item_id: '22918408-67CC-4093-80E8-BF6FA8915C21', variable_name: 'body' },
    { item_id: '63ABC96E-3804-4E1F-B38D-AABA3237B2D8', variable_name: 'mind' },
    { item_id: '72F93730-134F-4CA2-8FC4-F2915F20CAC6', variable_name: 'soul' },
];
const SPEED_TYPES: ReadonlyArray<{ item_id: string; variable_name: string }> = [
    { item_id: 'DF516479-F164-87FA-7BB8-6F268BF1DCE7', variable_name: 'speed_walking' },
    { item_id: 'B96061AE-3369-7E7A-B6A2-C7C8FBF2A23F', variable_name: 'speed_jumping' },
    { item_id: 'ED9810A9-3456-4D6B-BC5C-A1BAF5952B98', variable_name: 'speed_swimming' },
    { item_id: 'EDCBE2A8-CD3D-43DC-9B92-84A634BE12AE', variable_name: 'speed_climbing' },
    { item_id: 'F1D0A27A-43F1-4A4C-A5F8-ED9BD2D908D4', variable_name: 'speed_flying' },
    { item_id: 'ACFB413D-15A8-835B-C821-9E101B6C4F60', variable_name: 'speed_burrowing' },
    { item_id: '3C96DE74-D9E6-4173-8671-989209A0BF97', variable_name: 'teleport' },
    { item_id: 'AE7D4EB3-DC36-BD47-978F-1DBCFE2B616A', variable_name: 'speed_hovering' },
];
const SENSES: ReadonlyArray<{ item_id: string; variable_name: string }> = [
    { item_id: '3B58A8C2-F310-42FB-A71C-904904089303', variable_name: 'nightsight' },
    { item_id: 'F87E66D1-0EB2-469F-BB07-CF2CF4E26B30', variable_name: 'blindsight' },
    { item_id: '51EFBF8C-672F-F1F8-543A-6B079320572C', variable_name: 'fiendsight' },
    { item_id: 'E472D6BD-1A9C-F84E-AD55-F3068A3EC303', variable_name: 'ghostsight' },
    { item_id: '6B8E5648-B589-5F6A-DFAA-84F7E646B062', variable_name: 'spellsight' },
    { item_id: '9F34BE02-A199-4A9C-A2C4-531CD8A1DD58', variable_name: 'truesight' },
    { item_id: '61FD588C-4A17-4190-999B-7832820C44BA', variable_name: 'tremorsense' },
    { item_id: '57DAF94A-7C3A-4C5B-A7A9-22729D06355F', variable_name: 'telepathy' },
];
const TOTAL_LEVEL_INFO_UUID = 'A0419AE8-7882-1A04-6094-DD75CAD79164';

type SummaryWindow = Window & {
    openItemInfo?: (itemId: string) => unknown;
    useAbility?: (itemId: string) => unknown;
    forceOpenSideInfoPanel?: () => unknown;
    forceOpenSideLog?: () => unknown;
    renderEssenceInfoPanel?: (casterId: string, variableName: string) => unknown;
    renderMovementSenseInfoPanel?: (casterId: string, variableName: string) => unknown;
    renderCreatureStatusForm?: (casterId: string) => unknown;
    queryData?: (url: string, targetId: string, payload: Record<string, string>) => unknown;
};

function summaryAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-summary-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function summaryInfoAttrs(itemId: string): string {
    return summaryAttrs('open-info', { itemId });
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-summary-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftSummaryAction ?? '';
    const w = window as SummaryWindow;
    if (action === 'open-info') {
        const itemId = el.dataset.ftItemId ?? '';
        if (itemId !== '') { event.preventDefault(); w.openItemInfo?.(itemId); }
    } else if (action === 'use-ability') {
        const itemId = el.dataset.ftItemId ?? '';
        if (itemId !== '') { event.preventDefault(); w.useAbility?.(itemId); }
    } else if (action === 'essence-info') {
        event.preventDefault();
        w.forceOpenSideInfoPanel?.();
        w.renderEssenceInfoPanel?.(el.dataset.ftCasterId ?? '', el.dataset.ftVariableName ?? '');
    } else if (action === 'movement-sense-info') {
        event.preventDefault();
        w.forceOpenSideInfoPanel?.();
        w.renderMovementSenseInfoPanel?.(el.dataset.ftCasterId ?? '', el.dataset.ftVariableName ?? '');
    } else if (action === 'status-form') {
        event.preventDefault();
        const casterId = el.dataset.ftCasterId ?? '';
        const encounterId = el.dataset.ftEncounterId ?? '';
        w.forceOpenSideLog?.();
        document.getElementById('pills_sidelog_form_tab')?.click();
        if (typeof w.renderCreatureStatusForm === 'function') {
            w.renderCreatureStatusForm(casterId);
        } else {
            w.queryData?.(`${BASE_URL}options/tools/statuses/forms/form_modify_creature_status_effects_inc.php`, 'div_refresh_sidelog_form', { caster_id: casterId, encounter_id: encounterId });
        }
    }
});

// ── Speed/sense badge gradients — kept in sync with info-panel/creature-summary.ts
// so the PC sheet summary bar matches the creature card. SPEED = cool blue-grey,
// SENSE = muted green, PREFERRED speed = gold/amber "highlighter" gradient + amber
// glow ring so the preferred_speed_type stands out from speeds AND green senses. ──
const SPEED_BADGE_STYLE = 'background:linear-gradient(135deg,#243043,#3c5573);color:#fff;';
const SENSE_BADGE_STYLE = 'background:linear-gradient(135deg,#1f3a2e,#356b4f);color:#fff;';
const PREFERRED_SPEED_BADGE_STYLE = 'background:linear-gradient(135deg,#f2a900,#ffd633);color:#2b2200;text-shadow:0 1px 1px rgba(255,255,255,.4);box-shadow:0 0 0 2px rgba(255,196,0,.6);border-color:#ffe893;';

function unwrap(v: unknown): unknown { return Array.isArray(v) ? v[0] : v; }
function num(v: unknown): number { const n = Number(unwrap(v)); return Number.isFinite(n) ? n : 0; }
function partTotal(p: unknown): number {
    if (p === null || p === undefined || typeof p === 'boolean') return 0;
    return typeof p === 'object' ? num((p as { total?: unknown }).total) : num(p);
}
function dashIfZero(n: number): string { return n === 0 ? '—' : String(n); }
/** Client port of can_level_up() (includes/functions.php:11324): the FIRST tier whose total_level
 *  exceeds the caster's current total_level is the next level; the caster can level when their
 *  earned marks meet that tier's cumulative total_marks. False at/above max level (40). Returns
 *  null when the blob lacks the inputs (total_level / experience_marks) so the caller can fall
 *  back to the PHP-mount data-attr — on a cache/realias render there IS no attr, and the blob is
 *  the same source PHP computes from, so this is authoritative whenever the inputs are present. */
function canLevelUpFromBlob(caster: CachedCaster): boolean | null {
    const lvlRaw = caster?.computed?.creature?.meta?.total_level ?? ftLayerValue(caster, 'total_level');
    const marksRaw = pcField(caster, 'experience_marks');
    if ((lvlRaw === undefined || lvlRaw === null) && (marksRaw === undefined || marksRaw === null)) return null;
    const currentLevel = num(lvlRaw);
    const marks = num(marksRaw);
    return canLevelUp(currentLevel, marks);
}
/** Normalize a speed-type pointer to its UUID. preferred_speed_type can arrive as a
 *  nested {items:{item_id}} object (the reshaped blob), a {item_id} shell, a [uuid]
 *  array, or a bare uuid string — pull the item_id out of whichever shape it is. */
function speedTypeId(v: unknown): string {
    if (v == null) return '';
    const u = unwrap(v);
    if (u && typeof u === 'object') {
        const o = u as Record<string, unknown>;
        const items = o.items as Record<string, unknown> | undefined;
        const id = (items && typeof items === 'object') ? items.item_id : o.item_id;
        return String(unwrap(id) ?? '');
    }
    return String(u ?? '');
}
function ec(caster: CachedCaster): Record<string, unknown> { return caster.encounter_creatures ?? {}; }
function essenceTotal(caster: CachedCaster, v: string): number { return partTotal(caster?.computed?.creature?.essences?.[v]); }
function speedTotal(caster: CachedCaster, v: string): number {
    const short = v === 'teleport' ? 'teleport' : v.slice('speed_'.length);
    return partTotal(caster?.computed?.creature?.speeds?.[short]);
}
function senseTotal(caster: CachedCaster, v: string): number { return partTotal(caster?.computed?.creature?.senses?.[v]); }
/** Raw player_characters slot. Batch-3 catalogization (2026-06-11) flipped these provider slots
 *  (class_N / subclass_N / ancestry_N / lineage) from a {items:{…}} nest to a BARE UUID STRING;
 *  warm pre-Batch-3 blobs may still carry the nest. */
function pcSlot(caster: CachedCaster, key: string): unknown { return caster.player_characters?.[key]; }
/** Resolve a PC provider slot to {id, name}, tolerant of both the bare-UUID-string shape and the
 *  legacy {items:{…}} nest. A bare string resolves its display name via the cross-root catalog
 *  (catalogNameById walks casterCache/objectCache/encounterCache) — the realiased encounter MEMBER
 *  has no catalog of its own, so the name lives on the encounter ROOT. Without this, ftItemsCol on
 *  a bare string returns undefined and every class/subclass/ancestry badge+icon silently drops. */
function pcSlotIdName(caster: CachedCaster, key: string): { id: string; name: string } {
    const slot = pcSlot(caster, key);
    if (slot === null || slot === undefined || slot === '') return { id: '', name: '' };
    if (typeof slot === 'string') return { id: slot, name: catalogNameById(slot) };
    return { id: String(ftItemsCol(slot, 'item_id') ?? ''), name: String(ftItemsCol(slot, 'item_name') ?? '') };
}
/** Merge picture lists from top + encounter_creatures + player_characters buckets, keyed by type
 *  (mirrors PHP ft_thumbnail_mount_pictures' top/ec/pc flatten). */
function flattenPictures(caster: CachedCaster): Record<string, unknown[]> {
    const out: Record<string, unknown[]> = {};
    const hosts: unknown[] = [caster, caster.encounter_creatures, caster.player_characters];
    for (const host of hosts) {
        const h = host !== null && typeof host === 'object' ? host as Record<string, unknown> : null;
        const hp = h?.pictures;
        if (!hp || typeof hp !== 'object' || Array.isArray(hp)) continue;
        for (const k of Object.keys(hp)) {
            const list = (hp as Record<string, unknown>)[k];
            if (Array.isArray(list) && list.length) out[k] = (out[k] || []).concat(list);
        }
    }
    // ROUND-9 #9: the EC presentation/thumbnail STAMP rows are elided when they duplicate the member's
    // own galleries — re-derive through picturesOf's EC-stamp bridge (member-root call) so the
    // 'encounter_creature_thumbnail' selector keeps resolving.
    for (const stampType of ['encounter_creature_presentation_picture', 'encounter_creature_thumbnail_picture']) {
        if (!out[stampType]) {
            const bridged = picturesOf(caster, stampType);
            if (bridged.length) out[stampType] = bridged;
        }
    }
    return out;
}

// ── header ──
function thumbPlaceholderId(id: string): string { return `ft_pc_summary_thumb_${id}`; }
export function classLevelIconsHtml(caster: CachedCaster, size = 50): string {
    return renderClassLevelIcons({ field: key => pcField(caster, key), id: key => pcSlotIdName(caster, key).id,
        escape: escapeHtml, attrs: summaryInfoAttrs, icon: (id, width) => iconMap(id, width, width) }, size);
}

function headerHtml(caster: CachedCaster, casterId: string, sideInfo: boolean, canLevelUp: boolean,
    lineageId: string, lineageName: string): string {
    const idAttr = escapeHtml(casterId);
    const levelTop = canLevelUp
        ? `<a class="btn btn-danger border" href="${BASE_URL}tools/player_characters/create_player_character.php?id=${idAttr}">${iconMap('level_up')}</a>`
        : `<h6><span class="badge bg-success border border-dark" role="button" ${summaryInfoAttrs(TOTAL_LEVEL_INFO_UUID)}>${escapeHtml(String(ftLayerValue(caster, 'total_level') ?? ''))}</span></h6>`;

    // player_name is persisted HTML-ENCODED like item_name — decode before escapeHtml, else a PC named
    // "Kel'Thuzad" renders the literal "&#039;". See ability-row.ts's note on the decode funnels.
    const name = escapeHtml(decodeHtmlEntitiesForPlainText(String(pcField(caster, 'player_name') ?? '')));

    const ancestryBadge = (slotKey: string): string => {
        const { id: aid, name: aname } = pcSlotIdName(caster, slotKey);
        if (aid === '' && aname === '') return '';
        return `<span role="button" class="badge bg-dark border" ${summaryInfoAttrs(aid)}>${escapeHtml(aname)}</span>`;
    };
    let ancestry = ancestryBadge('ancestry_1');
    if (pcSlot(caster, 'ancestry_2')) ancestry += ' ' + ancestryBadge('ancestry_2');
    if (lineageId !== '') ancestry += ` <span role="button" class="badge bg-warning" ${summaryInfoAttrs(lineageId)}>${escapeHtml(lineageName)}</span>`;

    const classIcons = classLevelIconsHtml(caster);

    const mainCls = sideInfo ? 'col-12 pr-1 pc-summary-main' : 'col-lg-3 col-md-5 col-sm-12 pr-1 pc-summary-main';
    return `<div class="${mainCls}">
        <div class="d-flex text-left align-items-start pc-summary-main-inner${sideInfo ? ' py-1 px-2' : ' p-2'}">
            <div class="text-center">
                <div class="${sideInfo ? 'mb-1' : 'mb-2'}"><span id="${thumbPlaceholderId(casterId)}"></span></div>
                <div>${levelTop}</div>
            </div>
            <div class="pl-2"><div class="d-flex flex-column bd-highlight">
                <div class="pb-1"><h5 class="m-0">${name}</h5></div>
                <div class="pb-1">${ancestry}</div>
                <div class="pb-1 pc-summary-main-classes">${classIcons}</div>
            </div></div>
        </div>
    </div>`;
}

// ── essences ──
function essencesHtml(caster: CachedCaster, sideInfo: boolean, canEdit: boolean): string {
    const cls = sideInfo
        ? 'd-flex justify-content-center align-items-center col-12 border-start border-end pc-summary-essences mb-2'
        : 'd-flex justify-content-center align-items-center col-lg-2 col-md-3 col-sm-12 px-1 border-start border-end pc-summary-essences';
    const ecId = escapeHtml(String(unwrap(ec(caster).encounter_creature_id) ?? ''));
    let cells = '';
    for (const e of ESSENCE_CORE) {
        const val = dashIfZero(essenceTotal(caster, e.variable_name));
        const valHtml = canEdit
            ? `<button class="btn btn-light btn-sm" type="button" ${summaryAttrs('use-ability', { itemId: e.item_id })} style="width:45px; height: 43.4px;">${escapeHtml(val)}</button>`
            : `<span class="d-inline-flex align-items-center justify-content-center border rounded user-select-none bg-body-secondary text-body-secondary" style="width:45px; height: 43.4px; font-size:0.85rem;">${escapeHtml(val)}</span>`;
        cells += `<div class="col-4 pr-1 pl-1">
            <div class="mb-2 text-center"><span role="button" ${summaryAttrs('essence-info', { casterId: String(unwrap(ec(caster).encounter_creature_id) ?? ''), variableName: e.variable_name })}>${iconMap(e.item_id)}</span></div>
            <div class="text-center">${valHtml}</div>
        </div>`;
    }
    return `<div class="${cls}">${cells}</div>`;
}

// ── speeds / senses ──
function speedSensesHtml(caster: CachedCaster, casterId: string, sideInfo: boolean): string {
    const idAttr = escapeHtml(casterId);
    const preferred = speedTypeId(ec(caster).preferred_speed_type) || speedTypeId(ftLayerValue(caster, 'default_preferred_speed_type'));
    const iconCell = (variable: string, itemId: string, val: number, badgeStyle: string): string =>
        `<div class="d-flex flex-column justify-content-center text-center col-3.5 p-1">
            <div><span role="button" data-ft-info-item-id="${escapeHtml(itemId)}" ${summaryAttrs('movement-sense-info', { casterId, variableName: variable })}>${iconMap(variable)}</span></div>
            <div><span class="badge border" style="${badgeStyle}">${val}</span></div>
        </div>`;

    let speeds = '';
    for (const s of SPEED_TYPES) {
        const v = speedTotal(caster, s.variable_name);
        if (v === 0) continue;
        speeds += iconCell(s.variable_name, s.item_id, v, preferred === s.item_id ? PREFERRED_SPEED_BADGE_STYLE : SPEED_BADGE_STYLE);
    }
    let senses = '';
    for (const s of SENSES) {
        const v = senseTotal(caster, s.variable_name);
        if (v === 0) continue;
        senses += iconCell(s.variable_name, s.item_id, v, SENSE_BADGE_STYLE);
    }

    const outerCls = sideInfo ? 'd-flex col-12 text-center pc-summary-speeds' : 'col-lg col-md-6 col-sm-12 px-1 text-center border-end pc-summary-speeds';
    const speedsCls = sideInfo ? 'd-flex justify-content-start col-6 border-end pc-summary-speeds__speeds' : 'd-flex justify-content-start border-bottom pc-summary-speeds__speeds';
    const sensesCls = sideInfo ? 'd-flex justify-content-start col-6 pc-summary-speeds__senses' : 'd-flex justify-content-start pc-summary-speeds__senses';
    return `<div class="${outerCls}"${sideInfo ? ' style="min-height: 65px;"' : ''}>
        <div class="${speedsCls}" style="min-height: 65px;">${speeds}</div>
        <div class="${sensesCls}" style="min-height: 65px;">${senses}</div>
    </div>`;
}

// ── status effects ──
function statusHtml(caster: CachedCaster, casterId: string, suffix: string, sideInfo: boolean, canEdit: boolean): string {
    const idAttr = escapeHtml(casterId);
    const encId = escapeHtml(String(unwrap(ec(caster).encounter_id) ?? ''));
    let icons = '';
    // TARGET-side statuses only: this section shows conditions afflicting THIS creature, not the
    // ones it has cast on others (caster-side). Mirrors the encounter creature sheet (creature-console).
    for (const se of ftCreatureStatuses(caster, 'target')) {
        const sid = String(ftItemsCol(se, 'item_id') ?? '');
        const sname = decodeHtmlEntitiesForPlainText(String(ftItemsCol(se, 'item_name') ?? ''));
        const rawDef = se !== null && typeof se === 'object' ? (se as Record<string, unknown>).status_arr : undefined;
        const def = rawDef !== null && typeof rawDef === 'object' && !Array.isArray(rawDef)
            ? rawDef as { items?: unknown; item_layer_types?: unknown; catalog?: unknown }
            : {};
        if (statusRunes(sid, def)) {
            // Non-standard status (applied from an ability/spell/curse → no registered icon): render its
            // L0 rune sigil instead of iconMap's "?" placeholder. A <button> wrapper makes the footer rune
            // scanner skip its own openRuneInfo click-wiring (it skips mounts inside a <button>), so the
            // click keeps the same openItemInfo target as the standard PNG statuses next to it.
            icons += `<button type="button" class="btn p-0 border-0 bg-transparent shadow-none lh-1 align-middle cursor-pointer" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${escapeHtml(sname)}" ${summaryInfoAttrs(sid)}>${statusRuneMountHtml(def)}</button>`;
        } else {
            icons += `<span class="cursor-pointer" ${summaryInfoAttrs(sid)}>${iconMap(sid, 25, 25, true, sname)}</span>`;
        }
    }
    const editBtn = canEdit
        ? `<span role="button" ${summaryAttrs('status-form', { casterId, encounterId: String(unwrap(ec(caster).encounter_id) ?? '') })}>${iconMap('edit')}</span>`
        : '';
    const cls = sideInfo ? 'col-12 pc-summary-status pc-summary-status--sideinfo' : 'col-lg col-md-6 col-sm-12 px-1 pc-summary-status';
    return `<div class="${cls}"${sideInfo ? ' style="min-height: 70px;"' : ''}>
        <div class="mb-2 text-center"><span class="fw-bold">STATUS EFFECTS</span> ${editBtn}</div>
        <div id="div_refresh_status_effects${suffix}">${icons}</div>
    </div>`;
}

export function renderSummary(caster: CachedCaster, casterId: string, container: HTMLElement): void {
    const sideInfo = container.getAttribute('data-ft-pc-side-info') === '1';
    // Compute from the blob first (correct on the cache/realias render, which emits no PHP
    // data-attr → it was hardcoding false and hiding the level-up button); fall back to the
    // PHP-mount attr only when the blob lacks the inputs.
    const canLevelUpBlob = canLevelUpFromBlob(caster);
    const canLevelUp = canLevelUpBlob !== null
        ? canLevelUpBlob
        : container.getAttribute('data-ft-pc-can-level-up') === '1';
    // Lineage from the blob: player_characters.lineage is a bare UUID post-Batch-3 (legacy nest
    // tolerated), name resolved cross-root — the PHP data-attrs are absent on cache/realias renders.
    const lineageBlob = pcSlotIdName(caster, 'lineage');
    const lineageId = lineageBlob.id || (container.getAttribute('data-ft-pc-lineage-id') ?? '');
    const lineageName = lineageBlob.name || (container.getAttribute('data-ft-pc-lineage-name') ?? '');
    const suffix = container.getAttribute('data-ft-pc-dom-suffix') ?? '';
    const canEdit = ftHasPlayerPermission(caster);
    // Status effects MIGRATED to the action bar's left-side cluster (renderActionBarStatusCluster) — but
    // only on the FULL sheet IN an encounter, which is exactly where the action bar (and its cluster) is
    // present. Keep the summary's own STATUS EFFECTS block as the fallback for the side-info drawer (no
    // action bar there) and for a non-encounter PC sheet (the cluster is empty without an encounter_creature_id).
    const inEncounter = String(unwrap(ec(caster).encounter_creature_id) ?? '') !== '';
    const statusInActionBar = !sideInfo && inEncounter;

    const barsId = `ft_pc_vmana_${casterId}`;
    const vmCls = sideInfo
        ? 'd-flex justify-content-center align-items-center col-12 border border-top-0 border-start border-end pc-summary-vm'
        : 'd-flex justify-content-center align-items-center col-lg-2 col-md-4 col-sm-12 px-1 border-end pc-summary-vm';

    const barClass = sideInfo
        ? 'd-flex flex-wrap align-items-center text-left border pc-summary-bar pc-summary-bar--sideinfo-host'
        : 'd-flex flex-row flex-wrap align-items-center text-left border pc-summary-bar pc-summary-bar--full-sheet';

    const html = `<div id="div_pc_summary_bar${suffix}" class="${barClass}">
        ${headerHtml(caster, casterId, sideInfo, canLevelUp, lineageId, lineageName)}
        ${essencesHtml(caster, sideInfo, canEdit)}
        <div class="${vmCls}"><div id="${barsId}"></div></div>
        ${speedSensesHtml(caster, casterId, sideInfo)}
        ${statusInActionBar ? '' : statusHtml(caster, casterId, suffix, sideInfo, canEdit)}
    </div>`;
    // An unchanged bar keeps its painted DOM and runes; the thumbnail rebuilds only when its own inputs change, and the
    // V/Mana/AC/Luck strip, which reads the cache rather than the markup, repaints every time.
    const thumb = {
        pictures: flattenPictures(caster),
        item_id: String(ftItemsCol(caster, 'item_id') ?? ''),
        name: String(pcField(caster, 'player_name') ?? ''),
        w: 60, h: 60,
        selector: 'encounter_creature_thumbnail',
        size: ftLayerValue(caster, 'size') ?? null,
    };
    const thumbKey = JSON.stringify(thumb);
    const held = paintedMarkupHolds(container, html);
    if (held && paintedThumbs.get(container) === thumbKey) {
        window.renderVManaEcacLuckBarsInline?.(casterId, barsId, sideInfo);
        return;
    }
    if (!held) {
        // Dispose tooltips on the outgoing summary bar before replacing it — otherwise Bootstrap's hover/
        // focus listeners keep firing on the detached icons → `_isWithActiveTrigger` null-_activeTrigger throw.
        window.disposeBootstrapTooltipsWithin?.(container);
        paintMarkup(container, html);
        paintedThumbs.delete(container); // the new bar's thumbnail host is empty until a build succeeds
        // Hydrate any non-standard status runes statusHtml() emitted (.ft-rune-mount placeholders). The
        // caster ROOT catalog is already stamped on the engine by renderPcSheetPane before this paint, so
        // name1() resolves reference layers; re-stamp defensively in case renderSummary is called directly.
        try {
            const cat = caster.catalog;
            if (cat && Object.keys(cat).length) window.RuneIcon?.setCatalog?.(cat);
        } catch { /* engine not ready */ }
        window.ftMountRuneIcons?.(container);
    }

    // Thumbnail (reuse the thumbnail bundle's builder; selector picks the creature thumbnail picture).
    // Pictures live per-bucket under <bucket>.pictures.<type> (top / encounter_creatures / player_characters);
    // flatten them like the PHP ft_thumbnail_mount_pictures so the selector finds encounter_creature_thumbnail_picture.
    const thumbHost = container.querySelector(`#${CSS.escape(thumbPlaceholderId(casterId))}`);
    const ft = window.FtThumbnail;
    if (thumbHost && ft && typeof ft.build === 'function') {
        try {
            thumbHost.replaceChildren(ft.build(thumb));
            paintedThumbs.set(container, thumbKey);
        } catch { /* leave the placeholder empty on a bad build */ }
    }

    // V/Mana/AC/Luck strip (reuse the info-panel renderer; reads the fresh cache).
    window.renderVManaEcacLuckBarsInline?.(casterId, barsId, sideInfo);
}
